import { useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { getLocale } from "@gamedev-web/gamedev-sdk-core";

import { xboxOnboardSiteLocationPrefix } from "../constants/urls";
import { AssistantErrorCode, assistantErrorCodes } from "../models/supportAssistant";
import type { CreateSupportCaseRequest } from "../models/supportCases";

export const assistantApiPath = `${xboxOnboardSiteLocationPrefix}/assistant/api`;
export const maxQuestionLength = 2000;
export const maxHistoryMessages = 20;
const maxTitleLength = 80;

export type AssistantLink = Readonly<{ title: string; url: string }>;
export type EscalationTarget = "supportTicket" | "devkitSetup" | "accountVerification";
export type AssistantEscalation = Readonly<{
  target: EscalationTarget;
  summary: string;
  /** Drafted by Nexus, which still emits the legacy hub `category`; see legacyCategorySubjectIds. */
  ticket?: (Omit<CreateSupportCaseRequest, "subjectId"> & Readonly<{ category?: string }>) | null;
}>;
export type AssistantRating = "helpful" | "notHelpful";
export type AssistantWarning = "DOCS_UNAVAILABLE" | "CONTEXT_UNAVAILABLE";

export type AssistantAnswer = Readonly<{
  correlationId: string;
  markdown: string;
  citations: AssistantLink[];
  sourcesConsulted: AssistantLink[];
  escalation: AssistantEscalation | null;
  suggestedFollowUps: string[];
  warnings: AssistantWarning[];
  rating?: AssistantRating;
}>;

export type AssistantMessage = Readonly<{
  role: "user" | "assistant";
  text: string;
  answer?: AssistantAnswer;
  error?: AssistantErrorCode;
}>;

export type GameScope = Readonly<{ id: string; name: string }>;
export type AssistantHomeData = Readonly<{ stage: string; games: GameScope[] }>;
export type AssistantView = "home" | "chat";

export type SupportAssistantState = Readonly<{
  title: string;
  gameScope: GameScope | null;
  messages: AssistantMessage[];
  pending: boolean;
  view: AssistantView;
  home?: AssistantHomeData;
}>;

export type AssistantRequestContext = Readonly<{ page: string; locale: string }>;

type MessagesResponse = Partial<{
  correlationId: string;
  answer: string;
  citations: AssistantLink[];
  sourcesConsulted: AssistantLink[];
  escalation: AssistantEscalation | null;
  suggestedFollowUps: string[];
  warnings: AssistantWarning[];
  error: string;
}>;

const emptyConversation: Pick<SupportAssistantState, "title" | "messages" | "pending" | "view"> = {
  title: "",
  messages: [],
  pending: false,
  view: "home",
};

// Memory only: survives SPA navigation, gone on refresh, tab close, or sign-out (FR-018);
// sign-out is a full-page navigation to /auth/logout.
let state: SupportAssistantState = { gameScope: null, ...emptyConversation };
let controller: AbortController | undefined;
let homeRequest: Promise<void> | undefined;
const listeners = new Set<() => void>();

const setState = (update: Partial<SupportAssistantState>) => {
  state = { ...state, ...update };
  listeners.forEach(listener => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const getSnapshot = () => state;

const post = (path: string, body: unknown, signal?: AbortSignal) =>
  fetch(`${assistantApiPath}${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });

const toErrorCode = (code: unknown): AssistantErrorCode =>
  assistantErrorCodes.find(known => known === code) ?? "UNAVAILABLE";

const toAnswer = (body: MessagesResponse): AssistantAnswer => ({
  correlationId: body.correlationId ?? "",
  markdown: body.answer ?? "",
  citations: body.citations ?? [],
  sourcesConsulted: body.sourcesConsulted ?? [],
  escalation: body.escalation ?? null,
  suggestedFollowUps: body.suggestedFollowUps ?? [],
  warnings: body.warnings ?? [],
});

/** Last 20 successful turns, in the `{ role, text }` shape the hub API accepts. */
export const getHistory = (messages: ReadonlyArray<AssistantMessage>) =>
  messages
    .filter(message => !message.error)
    .slice(-maxHistoryMessages)
    .map(({ role, text }) => ({ role, text }));

const request = async (context: AssistantRequestContext) => {
  controller?.abort();
  const current = new AbortController();
  controller = current;
  setState({ pending: true, view: "chat" });
  let reply: AssistantMessage;
  try {
    const response = await post(
      "/messages",
      {
        messages: getHistory(state.messages),
        page: context.page,
        locale: context.locale,
        gameConceptId: state.gameScope?.id ?? null,
      },
      current.signal
    );
    const body = (await response.json().catch(() => ({}))) as MessagesResponse;
    reply = response.ok
      ? { role: "assistant", text: body.answer ?? "", answer: toAnswer(body) }
      : {
          role: "assistant",
          text: "",
          // The hub rate limiter answers 429 without an assistant error code.
          error: response.status === 429 ? "RATE_LIMITED" : toErrorCode(body.error),
        };
  } catch {
    reply = { role: "assistant", text: "", error: "UNAVAILABLE" };
  }
  // Cancelled, cleared, or superseded while waiting.
  if (controller !== current) {
    return;
  }
  controller = undefined;
  setState({ messages: [...state.messages, reply], pending: false });
};

const send = (text: string, context: AssistantRequestContext) => {
  const question = text.trim().slice(0, maxQuestionLength);
  if (!question || state.pending) {
    return;
  }
  setState({
    title: state.title || question.slice(0, maxTitleLength),
    messages: [...state.messages, { role: "user", text: question }],
  });
  request(context);
};

const retry = (context: AssistantRequestContext) => {
  if (state.pending || !state.messages[state.messages.length - 1]?.error) {
    return;
  }
  setState({ messages: state.messages.slice(0, -1) });
  request(context);
};

const cancel = () => {
  controller?.abort();
  controller = undefined;
  setState({ pending: false });
};

const clear = () => {
  cancel();
  setState(emptyConversation);
};

const rate = (correlationId: string, rating: AssistantRating) => {
  setState({
    messages: state.messages.map(message =>
      message.answer?.correlationId === correlationId
        ? { ...message, answer: { ...message.answer, rating } }
        : message
    ),
  });
  post("/feedback", { correlationId, rating }).catch(() => undefined);
};

/** Loads the stage and game list once per page load; failures use default content and retry on the next mount. */
const loadHome = () =>
  (homeRequest ??= fetch(`${assistantApiPath}/home`, { credentials: "same-origin" })
    .then(response => (response.ok ? (response.json() as Promise<AssistantHomeData>) : undefined))
    .catch(() => undefined)
    .then(data => {
      if (!data) {
        homeRequest = undefined;
      }
      setState({ home: { stage: data?.stage ?? "unknown", games: data?.games ?? [] } });
    }));

export const supportAssistantStore = {
  cancel,
  clear,
  getSnapshot,
  loadHome,
  rate,
  retry,
  send,
  setGameScope: (gameScope: GameScope | null) => setState({ gameScope }),
  setView: (view: AssistantView) => setState({ view }),
  subscribe,
};

/** Current conversation plus the page and locale to send with it. */
export const useSupportAssistant = () => {
  const locale = useSelector(getLocale);
  const page = useLocation().pathname;
  const state = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { state, context: { page, locale } };
};
